import { __awaiter } from "tslib";
import { NodeLiveOutputTypes } from "../../plugins/dataflow/model/utilities/node";
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();
function log(message) {
    // eslint-disable-next-line no-console
    console.log(`[SerialDevice] ${message}`);
}
export class SerialDevice {
    constructor() {
        var _a, _b;
        Object.defineProperty(this, "localBuffer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "port", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "connectChangeStamp", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lastConnectMessage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "deviceInfo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "serialNodesCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "writer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "serialModalShown", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "deviceFamily", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "READ_TIMEOUT_MS", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 2000
        });
        this.localBuffer = "";
        (_a = navigator.serial) === null || _a === void 0 ? void 0 : _a.addEventListener("connect", (e) => {
            this.updateConnectionInfo(e.timeStamp, e.type);
        });
        (_b = navigator.serial) === null || _b === void 0 ? void 0 : _b.addEventListener("disconnect", (e) => {
            this.updateConnectionInfo(e.timeStamp, e.type);
            this.deviceFamily = undefined;
        });
    }
    setSerialNodesCount(n) {
        this.serialNodesCount = n;
    }
    // TODO, revise this so it is more clear how its used
    updateConnectionInfo(timeStamp, status) {
        this.connectChangeStamp = timeStamp;
        this.lastConnectMessage = status;
        localStorage.setItem("last-connect-message", status);
    }
    determineDeviceFamily(info) {
        return info.usbProductId === 516 && info.usbVendorId === 3368
            ? "microbit"
            : "arduino";
    }
    hasPort() {
        var _a;
        return this.port !== undefined && ((_a = this.port) === null || _a === void 0 ? void 0 : _a.readable);
    }
    requestAndSetPort() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.port = yield navigator.serial.requestPort();
                this.deviceInfo = yield this.port.getInfo();
                this.deviceFamily = this.determineDeviceFamily(this.deviceInfo);
            }
            catch (error) {
                console.error("error requesting port: ", error);
            }
        });
    }
    readWithTimeout(streamReader) {
        return __awaiter(this, void 0, void 0, function* () {
            const timeoutPromise = new Promise((resolve) => {
                setTimeout(() => resolve({ value: undefined, done: false, timedOut: true }), this.READ_TIMEOUT_MS);
            });
            const readPromise = streamReader.read().then(({ value, done }) => ({
                value: value ? textDecoder.decode(value) : undefined,
                done,
                timedOut: false
            }));
            return Promise.race([readPromise, timeoutPromise]);
        });
    }
    closePort(streamReader) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.port)
                return;
            try {
                // Cancel and release the reader first
                if (streamReader) {
                    try {
                        yield streamReader.cancel();
                    }
                    catch (e) {
                        // Reader may already be released
                    }
                    try {
                        streamReader.releaseLock();
                    }
                    catch (e) {
                        // Lock may already be released
                    }
                }
                // Close the writer
                if (this.writer) {
                    try {
                        yield this.writer.close();
                    }
                    catch (e) {
                        // Writer may already be closed
                    }
                }
                yield this.port.close();
            }
            catch (e) {
                console.error("Error closing port:", e);
            }
        });
    }
    reopenPort() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.port)
                return false;
            try {
                yield this.port.open({ baudRate: 9600 });
                // Re-setup writer after reopening
                // If port.writable is null, this will throw and be caught below
                this.writer = this.port.writable.getWriter();
                return true;
            }
            catch (e) {
                console.error("Error reopening port:", e);
                return false;
            }
        });
    }
    handleStream(channels) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.port)
                return;
            yield this.port.open({ baudRate: 9600 }).catch((e) => console.error(e));
            // set up writer directly on the port's writable stream
            if (!this.port.writable) {
                console.error("Port is not writable");
                // This has never happened in practice, but we try to handle it gracefully
                yield this.closePort();
                return;
            }
            this.writer = this.port.writable.getWriter();
            // listen for serial data coming in to computer
            while (this.port) {
                if (!this.port.readable) {
                    // Port is closed, try to reopen
                    log("Port not readable, attempting to reopen...");
                    const reopened = yield this.reopenPort();
                    if (!reopened || !this.port.readable) {
                        console.error("Failed to reopen port, stopping stream handler");
                        break;
                    }
                }
                const streamReader = this.port.readable.getReader();
                try {
                    while (this.port.readable) {
                        const { value, done, timedOut } = yield this.readWithTimeout(streamReader);
                        if (timedOut) {
                            log("Read timed out, closing and reopening port...");
                            yield this.closePort(streamReader);
                            break; // Break inner loop to trigger reopen in outer loop
                        }
                        if (done) {
                            break;
                        }
                        if (this.deviceFamily === "arduino") {
                            this.handleArduinoStreamObj(value, channels);
                        }
                        if (this.deviceFamily === "microbit") {
                            this.handleMicroBitStreamObj(value, channels);
                        }
                    }
                }
                catch (error) {
                    console.error(error);
                }
                finally {
                    streamReader.releaseLock();
                }
            }
        });
    }
    handleMicroBitStreamObj(value, channels) {
        this.localBuffer += value;
        // [sc]   signal or control
        // [abcd] which micro:bit
        // [rth]  relay, temp, humidity
        const pattern = /([sc]{1})([abcd]{1})([rth]{1})([0-9.]+)\s{0,}[\r][\n]/g;
        let match;
        do {
            match = pattern.exec(this.localBuffer);
            if (!match)
                break;
            const [fullMatch, signalType, microbitId, element, reading] = match;
            this.localBuffer = this.localBuffer.substring(match.index + fullMatch.length);
            const targetChannelId = `${element}-${microbitId}`;
            const targetChannel = channels.find((c) => {
                return c.channelId === targetChannelId;
            });
            if (targetChannel && signalType === "s") {
                if (["h", "t"].includes(element)) {
                    // handle message from a humidity or temperature sensor
                    if (isFinite(Number(reading))) {
                        targetChannel.value = Number(reading);
                    }
                    targetChannel.lastMessageReceivedAt = Date.now();
                }
                if (["r"].includes(element)) {
                    // handle message about relays state
                    targetChannel.relaysState = reading.split('').map(s => Number(s));
                    targetChannel.lastMessageReceivedAt = Date.now();
                }
            }
        } while (match);
    }
    handleArduinoStreamObj(value, channels) {
        this.localBuffer += value;
        // Match any alphanumeric channel name, not just known ones,
        // so unknown channels are still consumed from the buffer.
        // No 'g' flag since we always search from the start of the modified buffer.
        // Note this pattern doesn't handle NaN or negative numbers.
        // We are seeing at least some NAN values from the Arduino. This currently
        // gets ignored correctly, by the corrupted data fallback.
        const pattern = /([a-z0-9]+):([0-9.]+)[\r][\n]/;
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const match = pattern.exec(this.localBuffer);
            if (match) {
                const [fullMatch, channel, numStr] = match;
                this.localBuffer = this.localBuffer.substring(match.index + fullMatch.length);
                const targetChannel = channels.find((c) => {
                    return c.channelId === channel;
                });
                if (targetChannel) {
                    targetChannel.value = Math.round(Number(numStr));
                }
            }
            else {
                // No valid pattern found - check for corrupted data we can discard
                const lineEndIndex = this.localBuffer.indexOf("\r\n");
                if (lineEndIndex !== -1) {
                    // Discard everything up to and including the \r\n to recover
                    this.localBuffer = this.localBuffer.substring(lineEndIndex + 2);
                }
                else {
                    // No complete line to discard, wait for more data
                    break;
                }
            }
        }
    }
    writeLine(line) {
        if (this.hasPort()) {
            this.writer.write(textEncoder.encode(`${line}\n`));
        }
        else {
            log("Port closed, skipping write");
        }
    }
    writeToOutForMicroBitRelayHub(data, hubId, relayType) {
        const ri = NodeLiveOutputTypes.filter((ot) => ot.name === relayType)[0].relayIndex;
        const controlMessage = `c${hubId}${ri}${data}`;
        this.writeLine(controlMessage);
    }
    writeToOutForBBGripper(n, liveOutputType) {
        const outputConfig = NodeLiveOutputTypes.find(o => o.name === liveOutputType);
        if (this.hasPort() && (outputConfig === null || outputConfig === void 0 ? void 0 : outputConfig.angleBase) !== undefined) {
            const percent = n / 100;
            const openTo = Math.round(outputConfig.angleBase - (percent * outputConfig.sweep));
            this.writeLine(openTo.toString());
        }
    }
    writeToOutForServo(n, liveOutputType) {
        const outputConfig = NodeLiveOutputTypes.find(o => o.name === liveOutputType);
        if (this.hasPort() && (outputConfig === null || outputConfig === void 0 ? void 0 : outputConfig.angleOffset) !== undefined) {
            const scaledAngle = (outputConfig.angleScale * n) + outputConfig.angleOffset;
            const roundedScaled = Math.round(scaledAngle);
            this.writeLine(roundedScaled.toString());
        }
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoic2VyaWFsLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vLi4vc3JjL21vZGVscy9zdG9yZXMvc2VyaWFsLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiI7QUFDQSxPQUFPLEVBQUUsbUJBQW1CLEVBQUUsTUFBTSw2Q0FBNkMsQ0FBQztBQUVsRixNQUFNLFdBQVcsR0FBRyxJQUFJLFdBQVcsRUFBRSxDQUFDO0FBQ3RDLE1BQU0sV0FBVyxHQUFHLElBQUksV0FBVyxFQUFFLENBQUM7QUFFdEMsU0FBUyxHQUFHLENBQUMsT0FBZTtJQUMxQixzQ0FBc0M7SUFDdEMsT0FBTyxDQUFDLEdBQUcsQ0FBQyxrQkFBa0IsT0FBTyxFQUFFLENBQUMsQ0FBQztBQUMzQyxDQUFDO0FBRUQsTUFBTSxPQUFPLFlBQVk7SUFXdkI7O1FBVkE7Ozs7O1dBQW9CO1FBQ1o7Ozs7O1dBQXdCO1FBQ2hDOzs7OztXQUFrQztRQUNsQzs7Ozs7V0FBa0M7UUFDbEM7Ozs7O1dBQWtDO1FBQ2xDOzs7OztXQUF5QjtRQUN6Qjs7Ozs7V0FBb0M7UUFDcEM7Ozs7O1dBQWlDO1FBQ2pDOzs7OztXQUFpQztRQStDaEI7Ozs7bUJBQWtCLElBQUk7V0FBQztRQTVDdEMsSUFBSSxDQUFDLFdBQVcsR0FBRyxFQUFFLENBQUM7UUFFdEIsTUFBQSxTQUFTLENBQUMsTUFBTSwwQ0FBRSxnQkFBZ0IsQ0FBQyxTQUFTLEVBQUUsQ0FBQyxDQUFDLEVBQUUsRUFBRTtZQUNsRCxJQUFJLENBQUMsb0JBQW9CLENBQUMsQ0FBQyxDQUFDLFNBQVMsRUFBRSxDQUFDLENBQUMsSUFBSSxDQUFDLENBQUM7UUFDakQsQ0FBQyxDQUFDLENBQUM7UUFFSCxNQUFBLFNBQVMsQ0FBQyxNQUFNLDBDQUFFLGdCQUFnQixDQUFDLFlBQVksRUFBRSxDQUFDLENBQUMsRUFBRSxFQUFFO1lBQ3JELElBQUksQ0FBQyxvQkFBb0IsQ0FBQyxDQUFDLENBQUMsU0FBUyxFQUFFLENBQUMsQ0FBQyxJQUFJLENBQUMsQ0FBQztZQUMvQyxJQUFJLENBQUMsWUFBWSxHQUFHLFNBQVMsQ0FBQztRQUNoQyxDQUFDLENBQUMsQ0FBQztJQUNMLENBQUM7SUFFTSxtQkFBbUIsQ0FBQyxDQUFTO1FBQ2xDLElBQUksQ0FBQyxnQkFBZ0IsR0FBRyxDQUFDLENBQUM7SUFDNUIsQ0FBQztJQUVELHFEQUFxRDtJQUM5QyxvQkFBb0IsQ0FBQyxTQUF3QixFQUFFLE1BQWM7UUFDbEUsSUFBSSxDQUFDLGtCQUFrQixHQUFHLFNBQVMsQ0FBQztRQUNwQyxJQUFJLENBQUMsa0JBQWtCLEdBQUcsTUFBTSxDQUFDO1FBQ2pDLFlBQVksQ0FBQyxPQUFPLENBQUMsc0JBQXNCLEVBQUUsTUFBTSxDQUFDLENBQUM7SUFDdkQsQ0FBQztJQUVNLHFCQUFxQixDQUFDLElBQW9CO1FBQy9DLE9BQU8sSUFBSSxDQUFDLFlBQVksS0FBSyxHQUFHLElBQUksSUFBSSxDQUFDLFdBQVcsS0FBSyxJQUFJO1lBQzNELENBQUMsQ0FBQyxVQUFVO1lBQ1osQ0FBQyxDQUFDLFNBQVMsQ0FBQztJQUNoQixDQUFDO0lBRU0sT0FBTzs7UUFDWixPQUFPLElBQUksQ0FBQyxJQUFJLEtBQUssU0FBUyxLQUFJLE1BQUEsSUFBSSxDQUFDLElBQUksMENBQUUsUUFBUSxDQUFBLENBQUM7SUFDeEQsQ0FBQztJQUVZLGlCQUFpQjs7WUFDNUIsSUFBSSxDQUFDO2dCQUNILElBQUksQ0FBQyxJQUFJLEdBQUcsTUFBTSxTQUFTLENBQUMsTUFBTSxDQUFDLFdBQVcsRUFBRSxDQUFDO2dCQUNqRCxJQUFJLENBQUMsVUFBVSxHQUFHLE1BQU0sSUFBSSxDQUFDLElBQUksQ0FBQyxPQUFPLEVBQUUsQ0FBQztnQkFDNUMsSUFBSSxDQUFDLFlBQVksR0FBRyxJQUFJLENBQUMscUJBQXFCLENBQUMsSUFBSSxDQUFDLFVBQVUsQ0FBQyxDQUFDO1lBQ2xFLENBQUM7WUFDRCxPQUFPLEtBQUssRUFBRSxDQUFDO2dCQUNiLE9BQU8sQ0FBQyxLQUFLLENBQUMseUJBQXlCLEVBQUUsS0FBSyxDQUFDLENBQUM7WUFDbEQsQ0FBQztRQUNILENBQUM7S0FBQTtJQUlhLGVBQWUsQ0FDM0IsWUFBcUQ7O1lBRXJELE1BQU0sY0FBYyxHQUFHLElBQUksT0FBTyxDQUFvRCxDQUFDLE9BQU8sRUFBRSxFQUFFO2dCQUNoRyxVQUFVLENBQUMsR0FBRyxFQUFFLENBQUMsT0FBTyxDQUFDLEVBQUUsS0FBSyxFQUFFLFNBQVMsRUFBRSxJQUFJLEVBQUUsS0FBSyxFQUFFLFFBQVEsRUFBRSxJQUFJLEVBQUUsQ0FBQyxFQUFFLElBQUksQ0FBQyxlQUFlLENBQUMsQ0FBQztZQUNyRyxDQUFDLENBQUMsQ0FBQztZQUVILE1BQU0sV0FBVyxHQUFHLFlBQVksQ0FBQyxJQUFJLEVBQUUsQ0FBQyxJQUFJLENBQUMsQ0FBQyxFQUFFLEtBQUssRUFBRSxJQUFJLEVBQUUsRUFBRSxFQUFFLENBQUMsQ0FBQztnQkFDakUsS0FBSyxFQUFFLEtBQUssQ0FBQyxDQUFDLENBQUMsV0FBVyxDQUFDLE1BQU0sQ0FBQyxLQUFLLENBQUMsQ0FBQyxDQUFDLENBQUMsU0FBUztnQkFDcEQsSUFBSTtnQkFDSixRQUFRLEVBQUUsS0FBSzthQUNoQixDQUFDLENBQUMsQ0FBQztZQUVKLE9BQU8sT0FBTyxDQUFDLElBQUksQ0FBQyxDQUFDLFdBQVcsRUFBRSxjQUFjLENBQUMsQ0FBQyxDQUFDO1FBQ3JELENBQUM7S0FBQTtJQUVhLFNBQVMsQ0FBQyxZQUFzRDs7WUFDNUUsSUFBSSxDQUFDLElBQUksQ0FBQyxJQUFJO2dCQUFFLE9BQU87WUFDdkIsSUFBSSxDQUFDO2dCQUNILHNDQUFzQztnQkFDdEMsSUFBSSxZQUFZLEVBQUUsQ0FBQztvQkFDakIsSUFBSSxDQUFDO3dCQUNILE1BQU0sWUFBWSxDQUFDLE1BQU0sRUFBRSxDQUFDO29CQUM5QixDQUFDO29CQUFDLE9BQU8sQ0FBQyxFQUFFLENBQUM7d0JBQ1gsaUNBQWlDO29CQUNuQyxDQUFDO29CQUNELElBQUksQ0FBQzt3QkFDSCxZQUFZLENBQUMsV0FBVyxFQUFFLENBQUM7b0JBQzdCLENBQUM7b0JBQUMsT0FBTyxDQUFDLEVBQUUsQ0FBQzt3QkFDWCwrQkFBK0I7b0JBQ2pDLENBQUM7Z0JBQ0gsQ0FBQztnQkFDRCxtQkFBbUI7Z0JBQ25CLElBQUksSUFBSSxDQUFDLE1BQU0sRUFBRSxDQUFDO29CQUNoQixJQUFJLENBQUM7d0JBQ0gsTUFBTSxJQUFJLENBQUMsTUFBTSxDQUFDLEtBQUssRUFBRSxDQUFDO29CQUM1QixDQUFDO29CQUFDLE9BQU8sQ0FBQyxFQUFFLENBQUM7d0JBQ1gsK0JBQStCO29CQUNqQyxDQUFDO2dCQUNILENBQUM7Z0JBQ0QsTUFBTSxJQUFJLENBQUMsSUFBSSxDQUFDLEtBQUssRUFBRSxDQUFDO1lBQzFCLENBQUM7WUFBQyxPQUFPLENBQUMsRUFBRSxDQUFDO2dCQUNYLE9BQU8sQ0FBQyxLQUFLLENBQUMscUJBQXFCLEVBQUUsQ0FBQyxDQUFDLENBQUM7WUFDMUMsQ0FBQztRQUNILENBQUM7S0FBQTtJQUVhLFVBQVU7O1lBQ3RCLElBQUksQ0FBQyxJQUFJLENBQUMsSUFBSTtnQkFBRSxPQUFPLEtBQUssQ0FBQztZQUM3QixJQUFJLENBQUM7Z0JBQ0gsTUFBTSxJQUFJLENBQUMsSUFBSSxDQUFDLElBQUksQ0FBQyxFQUFFLFFBQVEsRUFBRSxJQUFJLEVBQUUsQ0FBQyxDQUFDO2dCQUN6QyxrQ0FBa0M7Z0JBQ2xDLGdFQUFnRTtnQkFDaEUsSUFBSSxDQUFDLE1BQU0sR0FBRyxJQUFJLENBQUMsSUFBSSxDQUFDLFFBQVMsQ0FBQyxTQUFTLEVBQUUsQ0FBQztnQkFDOUMsT0FBTyxJQUFJLENBQUM7WUFDZCxDQUFDO1lBQUMsT0FBTyxDQUFDLEVBQUUsQ0FBQztnQkFDWCxPQUFPLENBQUMsS0FBSyxDQUFDLHVCQUF1QixFQUFFLENBQUMsQ0FBQyxDQUFDO2dCQUMxQyxPQUFPLEtBQUssQ0FBQztZQUNmLENBQUM7UUFDSCxDQUFDO0tBQUE7SUFFWSxZQUFZLENBQUMsUUFBZ0M7O1lBQ3hELElBQUksQ0FBQyxJQUFJLENBQUMsSUFBSTtnQkFBRSxPQUFPO1lBQ3ZCLE1BQU0sSUFBSSxDQUFDLElBQUksQ0FBQyxJQUFJLENBQUMsRUFBRSxRQUFRLEVBQUUsSUFBSSxFQUFFLENBQUMsQ0FBQyxLQUFLLENBQUMsQ0FBQyxDQUFNLEVBQUUsRUFBRSxDQUFDLE9BQU8sQ0FBQyxLQUFLLENBQUMsQ0FBQyxDQUFDLENBQUMsQ0FBQztZQUU3RSx1REFBdUQ7WUFDdkQsSUFBSSxDQUFDLElBQUksQ0FBQyxJQUFJLENBQUMsUUFBUSxFQUFFLENBQUM7Z0JBQ3hCLE9BQU8sQ0FBQyxLQUFLLENBQUMsc0JBQXNCLENBQUMsQ0FBQztnQkFDdEMsMEVBQTBFO2dCQUMxRSxNQUFNLElBQUksQ0FBQyxTQUFTLEVBQUUsQ0FBQztnQkFDdkIsT0FBTztZQUNULENBQUM7WUFDRCxJQUFJLENBQUMsTUFBTSxHQUFHLElBQUksQ0FBQyxJQUFJLENBQUMsUUFBUSxDQUFDLFNBQVMsRUFBRSxDQUFDO1lBRTdDLCtDQUErQztZQUMvQyxPQUFPLElBQUksQ0FBQyxJQUFJLEVBQUUsQ0FBQztnQkFDakIsSUFBSSxDQUFDLElBQUksQ0FBQyxJQUFJLENBQUMsUUFBUSxFQUFFLENBQUM7b0JBQ3hCLGdDQUFnQztvQkFDaEMsR0FBRyxDQUFDLDRDQUE0QyxDQUFDLENBQUM7b0JBQ2xELE1BQU0sUUFBUSxHQUFHLE1BQU0sSUFBSSxDQUFDLFVBQVUsRUFBRSxDQUFDO29CQUN6QyxJQUFJLENBQUMsUUFBUSxJQUFJLENBQUMsSUFBSSxDQUFDLElBQUksQ0FBQyxRQUFRLEVBQUUsQ0FBQzt3QkFDckMsT0FBTyxDQUFDLEtBQUssQ0FBQyxnREFBZ0QsQ0FBQyxDQUFDO3dCQUNoRSxNQUFNO29CQUNSLENBQUM7Z0JBQ0gsQ0FBQztnQkFFRCxNQUFNLFlBQVksR0FBRyxJQUFJLENBQUMsSUFBSSxDQUFDLFFBQVMsQ0FBQyxTQUFTLEVBQUUsQ0FBQztnQkFDckQsSUFBSSxDQUFDO29CQUNILE9BQU8sSUFBSSxDQUFDLElBQUksQ0FBQyxRQUFRLEVBQUUsQ0FBQzt3QkFDMUIsTUFBTSxFQUFFLEtBQUssRUFBRSxJQUFJLEVBQUUsUUFBUSxFQUFFLEdBQUcsTUFBTSxJQUFJLENBQUMsZUFBZSxDQUFDLFlBQVksQ0FBQyxDQUFDO3dCQUUzRSxJQUFJLFFBQVEsRUFBRSxDQUFDOzRCQUNiLEdBQUcsQ0FBQywrQ0FBK0MsQ0FBQyxDQUFDOzRCQUNyRCxNQUFNLElBQUksQ0FBQyxTQUFTLENBQUMsWUFBWSxDQUFDLENBQUM7NEJBQ25DLE1BQU0sQ0FBQyxtREFBbUQ7d0JBQzVELENBQUM7d0JBRUQsSUFBSSxJQUFJLEVBQUMsQ0FBQzs0QkFDUixNQUFNO3dCQUNSLENBQUM7d0JBQ0QsSUFBSSxJQUFJLENBQUMsWUFBWSxLQUFLLFNBQVMsRUFBQyxDQUFDOzRCQUNuQyxJQUFJLENBQUMsc0JBQXNCLENBQUMsS0FBTSxFQUFFLFFBQVEsQ0FBQyxDQUFDO3dCQUNoRCxDQUFDO3dCQUNELElBQUksSUFBSSxDQUFDLFlBQVksS0FBSyxVQUFVLEVBQUMsQ0FBQzs0QkFDcEMsSUFBSSxDQUFDLHVCQUF1QixDQUFDLEtBQU0sRUFBRSxRQUFRLENBQUMsQ0FBQzt3QkFDakQsQ0FBQztvQkFDSCxDQUFDO2dCQUNILENBQUM7Z0JBQ0QsT0FBTyxLQUFLLEVBQUUsQ0FBQztvQkFDYixPQUFPLENBQUMsS0FBSyxDQUFDLEtBQUssQ0FBQyxDQUFDO2dCQUN2QixDQUFDO3dCQUNPLENBQUM7b0JBQ1AsWUFBWSxDQUFDLFdBQVcsRUFBRSxDQUFDO2dCQUM3QixDQUFDO1lBQ0gsQ0FBQztRQUNILENBQUM7S0FBQTtJQUVNLHVCQUF1QixDQUFDLEtBQWEsRUFBRSxRQUFnQztRQUM1RSxJQUFJLENBQUMsV0FBVyxJQUFJLEtBQUssQ0FBQztRQUUxQiwyQkFBMkI7UUFDM0IseUJBQXlCO1FBQ3pCLCtCQUErQjtRQUMvQixNQUFNLE9BQU8sR0FBRyx3REFBd0QsQ0FBQztRQUN6RSxJQUFJLEtBQTZCLENBQUM7UUFFbEMsR0FBRyxDQUFDO1lBQ0YsS0FBSyxHQUFHLE9BQU8sQ0FBQyxJQUFJLENBQUMsSUFBSSxDQUFDLFdBQVcsQ0FBQyxDQUFDO1lBQ3ZDLElBQUksQ0FBQyxLQUFLO2dCQUFFLE1BQU07WUFFbEIsTUFBTSxDQUFDLFNBQVMsRUFBRSxVQUFVLEVBQUUsVUFBVSxFQUFFLE9BQU8sRUFBRSxPQUFPLENBQUMsR0FBRyxLQUFLLENBQUM7WUFDcEUsSUFBSSxDQUFDLFdBQVcsR0FBRyxJQUFJLENBQUMsV0FBVyxDQUFDLFNBQVMsQ0FBQyxLQUFLLENBQUMsS0FBSyxHQUFHLFNBQVMsQ0FBQyxNQUFNLENBQUMsQ0FBQztZQUU5RSxNQUFNLGVBQWUsR0FBRyxHQUFHLE9BQU8sSUFBSSxVQUFVLEVBQUUsQ0FBQztZQUNuRCxNQUFNLGFBQWEsR0FBRyxRQUFRLENBQUMsSUFBSSxDQUFDLENBQUMsQ0FBa0IsRUFBRSxFQUFFO2dCQUN6RCxPQUFPLENBQUMsQ0FBQyxTQUFTLEtBQUssZUFBZSxDQUFDO1lBQ3pDLENBQUMsQ0FBQyxDQUFDO1lBRUgsSUFBSSxhQUFhLElBQUksVUFBVSxLQUFLLEdBQUcsRUFBRSxDQUFDO2dCQUN4QyxJQUFJLENBQUMsR0FBRyxFQUFFLEdBQUcsQ0FBQyxDQUFDLFFBQVEsQ0FBQyxPQUFPLENBQUMsRUFBQyxDQUFDO29CQUNoQyx1REFBdUQ7b0JBQ3ZELElBQUksUUFBUSxDQUFDLE1BQU0sQ0FBQyxPQUFPLENBQUMsQ0FBQyxFQUFDLENBQUM7d0JBQzdCLGFBQWEsQ0FBQyxLQUFLLEdBQUcsTUFBTSxDQUFDLE9BQU8sQ0FBQyxDQUFDO29CQUN4QyxDQUFDO29CQUNELGFBQWEsQ0FBQyxxQkFBcUIsR0FBRyxJQUFJLENBQUMsR0FBRyxFQUFFLENBQUM7Z0JBQ25ELENBQUM7Z0JBQ0QsSUFBSSxDQUFDLEdBQUcsQ0FBQyxDQUFDLFFBQVEsQ0FBQyxPQUFPLENBQUMsRUFBQyxDQUFDO29CQUMzQixvQ0FBb0M7b0JBQ3BDLGFBQWEsQ0FBQyxXQUFXLEdBQUcsT0FBTyxDQUFDLEtBQUssQ0FBQyxFQUFFLENBQUMsQ0FBQyxHQUFHLENBQUMsQ0FBQyxDQUFDLEVBQUUsQ0FBQyxNQUFNLENBQUMsQ0FBQyxDQUFDLENBQUMsQ0FBQztvQkFDbEUsYUFBYSxDQUFDLHFCQUFxQixHQUFHLElBQUksQ0FBQyxHQUFHLEVBQUUsQ0FBQztnQkFDbkQsQ0FBQztZQUNILENBQUM7UUFDSCxDQUFDLFFBQVEsS0FBSyxFQUFFO0lBQ2xCLENBQUM7SUFFTSxzQkFBc0IsQ0FBQyxLQUFhLEVBQUUsUUFBZ0M7UUFDM0UsSUFBSSxDQUFDLFdBQVcsSUFBSSxLQUFLLENBQUM7UUFFMUIsNERBQTREO1FBQzVELDBEQUEwRDtRQUMxRCw0RUFBNEU7UUFDNUUsNERBQTREO1FBQzVELDBFQUEwRTtRQUMxRSwwREFBMEQ7UUFDMUQsTUFBTSxPQUFPLEdBQUcsK0JBQStCLENBQUM7UUFFaEQsaURBQWlEO1FBQ2pELE9BQU8sSUFBSSxFQUFFLENBQUM7WUFDWixNQUFNLEtBQUssR0FBRyxPQUFPLENBQUMsSUFBSSxDQUFDLElBQUksQ0FBQyxXQUFXLENBQUMsQ0FBQztZQUU3QyxJQUFJLEtBQUssRUFBRSxDQUFDO2dCQUNWLE1BQU0sQ0FBQyxTQUFTLEVBQUUsT0FBTyxFQUFFLE1BQU0sQ0FBQyxHQUFHLEtBQUssQ0FBQztnQkFDM0MsSUFBSSxDQUFDLFdBQVcsR0FBRyxJQUFJLENBQUMsV0FBVyxDQUFDLFNBQVMsQ0FBQyxLQUFLLENBQUMsS0FBSyxHQUFHLFNBQVMsQ0FBQyxNQUFNLENBQUMsQ0FBQztnQkFFOUUsTUFBTSxhQUFhLEdBQUcsUUFBUSxDQUFDLElBQUksQ0FBQyxDQUFDLENBQWtCLEVBQUUsRUFBRTtvQkFDekQsT0FBTyxDQUFDLENBQUMsU0FBUyxLQUFLLE9BQU8sQ0FBQztnQkFDakMsQ0FBQyxDQUFDLENBQUM7Z0JBRUgsSUFBSSxhQUFhLEVBQUMsQ0FBQztvQkFDakIsYUFBYSxDQUFDLEtBQUssR0FBRyxJQUFJLENBQUMsS0FBSyxDQUFDLE1BQU0sQ0FBQyxNQUFNLENBQUMsQ0FBQyxDQUFDO2dCQUNuRCxDQUFDO1lBQ0gsQ0FBQztpQkFBTSxDQUFDO2dCQUNOLG1FQUFtRTtnQkFDbkUsTUFBTSxZQUFZLEdBQUcsSUFBSSxDQUFDLFdBQVcsQ0FBQyxPQUFPLENBQUMsTUFBTSxDQUFDLENBQUM7Z0JBQ3RELElBQUksWUFBWSxLQUFLLENBQUMsQ0FBQyxFQUFFLENBQUM7b0JBRXhCLDZEQUE2RDtvQkFDN0QsSUFBSSxDQUFDLFdBQVcsR0FBRyxJQUFJLENBQUMsV0FBVyxDQUFDLFNBQVMsQ0FBQyxZQUFZLEdBQUcsQ0FBQyxDQUFDLENBQUM7Z0JBQ2xFLENBQUM7cUJBQU0sQ0FBQztvQkFDTixrREFBa0Q7b0JBQ2xELE1BQU07Z0JBQ1IsQ0FBQztZQUNILENBQUM7UUFDSCxDQUFDO0lBQ0gsQ0FBQztJQUVNLFNBQVMsQ0FBQyxJQUFZO1FBQzNCLElBQUksSUFBSSxDQUFDLE9BQU8sRUFBRSxFQUFDLENBQUM7WUFDbEIsSUFBSSxDQUFDLE1BQU0sQ0FBQyxLQUFLLENBQUMsV0FBVyxDQUFDLE1BQU0sQ0FBQyxHQUFHLElBQUksSUFBSSxDQUFDLENBQUMsQ0FBQztRQUNyRCxDQUFDO2FBQU0sQ0FBQztZQUNOLEdBQUcsQ0FBQyw2QkFBNkIsQ0FBQyxDQUFDO1FBQ3JDLENBQUM7SUFDSCxDQUFDO0lBRU0sNkJBQTZCLENBQUMsSUFBWSxFQUFFLEtBQWEsRUFBRSxTQUFpQjtRQUNqRixNQUFNLEVBQUUsR0FBRyxtQkFBbUIsQ0FBQyxNQUFNLENBQUMsQ0FBQyxFQUFNLEVBQUUsRUFBRSxDQUFDLEVBQUUsQ0FBQyxJQUFJLEtBQUssU0FBUyxDQUFDLENBQUMsQ0FBQyxDQUFDLENBQUMsVUFBVSxDQUFDO1FBQ3ZGLE1BQU0sY0FBYyxHQUFHLElBQUksS0FBSyxHQUFHLEVBQUUsR0FBRyxJQUFJLEVBQUUsQ0FBQztRQUMvQyxJQUFJLENBQUMsU0FBUyxDQUFDLGNBQWMsQ0FBQyxDQUFDO0lBQ2pDLENBQUM7SUFFTSxzQkFBc0IsQ0FBQyxDQUFRLEVBQUUsY0FBc0I7UUFDNUQsTUFBTSxZQUFZLEdBQUcsbUJBQW1CLENBQUMsSUFBSSxDQUFDLENBQUMsQ0FBQyxFQUFFLENBQUMsQ0FBQyxDQUFDLElBQUksS0FBSyxjQUFjLENBQUMsQ0FBQztRQUM5RSxJQUFJLElBQUksQ0FBQyxPQUFPLEVBQUUsSUFBSSxDQUFBLFlBQVksYUFBWixZQUFZLHVCQUFaLFlBQVksQ0FBRSxTQUFTLE1BQUssU0FBUyxFQUFDLENBQUM7WUFDM0QsTUFBTSxPQUFPLEdBQUcsQ0FBQyxHQUFHLEdBQUcsQ0FBQztZQUN4QixNQUFNLE1BQU0sR0FBRyxJQUFJLENBQUMsS0FBSyxDQUFDLFlBQVksQ0FBQyxTQUFTLEdBQUcsQ0FBQyxPQUFPLEdBQUcsWUFBWSxDQUFDLEtBQUssQ0FBQyxDQUFDLENBQUM7WUFDbkYsSUFBSSxDQUFDLFNBQVMsQ0FBQyxNQUFNLENBQUMsUUFBUSxFQUFFLENBQUMsQ0FBQztRQUNwQyxDQUFDO0lBQ0gsQ0FBQztJQUVNLGtCQUFrQixDQUFDLENBQVEsRUFBRSxjQUFzQjtRQUN4RCxNQUFNLFlBQVksR0FBRyxtQkFBbUIsQ0FBQyxJQUFJLENBQUMsQ0FBQyxDQUFDLEVBQUUsQ0FBQyxDQUFDLENBQUMsSUFBSSxLQUFLLGNBQWMsQ0FBQyxDQUFDO1FBQzlFLElBQUksSUFBSSxDQUFDLE9BQU8sRUFBRSxJQUFJLENBQUEsWUFBWSxhQUFaLFlBQVksdUJBQVosWUFBWSxDQUFFLFdBQVcsTUFBSyxTQUFTLEVBQUMsQ0FBQztZQUM3RCxNQUFNLFdBQVcsR0FBRyxDQUFDLFlBQVksQ0FBQyxVQUFVLEdBQUcsQ0FBQyxDQUFDLEdBQUcsWUFBWSxDQUFDLFdBQVcsQ0FBQztZQUM3RSxNQUFNLGFBQWEsR0FBRyxJQUFJLENBQUMsS0FBSyxDQUFDLFdBQVcsQ0FBQyxDQUFDO1lBQzlDLElBQUksQ0FBQyxTQUFTLENBQUMsYUFBYSxDQUFDLFFBQVEsRUFBRSxDQUFDLENBQUM7UUFDM0MsQ0FBQztJQUNILENBQUM7Q0FDRiJ9